// Public invite acceptance (architecture sprint-02 §10.3). Lives outside ProtectedRoute and the
// AppShell. Calls use skipAuth so the SDK never turns a 401 into a /login redirect here.
import { zodResolver } from '@hookform/resolvers/zod';
import { isValidTimeZone } from '@shared/calendar';
import { useAuth } from '@skaleminds/jig-auth-sdk-ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { PublicCardLayout } from '@/components/PublicCardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiClientError, apiRequest } from '@/lib/api';
import type { AcceptInviteResult, InviteLookup } from '@/types/invites';

type EndState = 'invalid' | 'expired' | 'cancelled' | 'used' | 'member';

const END_STATE_MESSAGES: Record<EndState, string> = {
  invalid: "This invite link isn't valid. It may have been replaced by a newer link. Ask your Admin for a new one.",
  expired: 'This invite link has expired. Ask your Admin for a new link.',
  cancelled: 'This invite has been cancelled. Ask your Admin if you think this is a mistake.',
  used: 'This invite has already been used.',
  member: 'You already have access to Clockit. Sign in instead.',
};

const ERROR_CODE_TO_STATE: Record<string, EndState> = {
  INVITE_INVALID: 'invalid',
  INVITE_EXPIRED: 'expired',
  INVITE_CANCELLED: 'cancelled',
  INVITE_USED: 'used',
  ALREADY_A_USER: 'member',
};

export const AUTO_SIGN_IN_FAILED_MESSAGE =
  "Your account is ready, but we couldn't sign you in automatically. Sign in with your email and the password you chose.";
export const EXISTING_ACCOUNT_MESSAGE = 'Your workspace account already exists. Sign in with your existing workspace password.';
// Sprint 8 (architecture §8.4): no counterpart in the original app, so the approved brief's wording.
export const JOIN_INTRO = 'You already have a Skaleminds workspace account.';
export const JOINED_MESSAGE =
  "You've joined Clockit. Sign in with your existing workspace password, or open Clockit from the workspace portal.";
export const EXISTING_ACCOUNT_NOTE = 'If you already have a Skaleminds workspace account, your existing password stays the same.';
const NETWORK_ERROR_MESSAGE = "Couldn't reach Clockit. Check your connection and try again.";
const ALERT_CLASSES = 'rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger-fg';

const acceptSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter your name').max(100, 'Use at most 100 characters'),
    password: z.string().min(8, 'Use at least 8 characters').max(128, 'Use at most 128 characters'),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { message: "Passwords don't match", path: ['confirm'] });
type AcceptValues = z.infer<typeof acceptSchema>;

function browserTimeZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimeZone(tz) ? tz : undefined;
  } catch {
    return undefined;
  }
}

function postJson<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, { method: 'POST', body: JSON.stringify(body), skipAuth: true });
}

function EndStateScreen({ state }: { state: EndState }): JSX.Element {
  return (
    <div className="space-y-4" data-testid={`invite-state-${state}`}>
      <p className="text-sm">{END_STATE_MESSAGES[state]}</p>
      {(state === 'used' || state === 'member') && (
        <Button asChild className="w-full">
          <Link to="/login">Go to sign in</Link>
        </Button>
      )}
    </div>
  );
}

function SignedInNotice({ email }: { email: string }): JSX.Element {
  const { logout } = useAuth();
  return (
    <div className="space-y-4">
      <p className="text-sm">You&apos;re signed in as {email}. Sign out to accept this invite.</p>
      <Button variant="outline" className="w-full" onClick={() => void logout()}>
        Sign out
      </Button>
    </div>
  );
}

interface AcceptFormProps {
  token: string;
  invite: { email: string; role: 'admin' | 'user'; projects?: InviteProjectLine[] };
  onEndState: (state: EndState) => void;
  /** accountExists was unknown (jig-core unreachable): tell existing account holders their password stays. */
  showExistingNote?: boolean;
}

type InviteProjectLine = { name: string; role: 'member' | 'supervisor' };

/** Sprint 12 (§9.1): one line per project grant the invite carries. */
export function InviteProjects({ projects }: { projects: readonly InviteProjectLine[] }): JSX.Element | null {
  if (projects.length === 0) return null;
  return (
    <ul className="space-y-0.5 text-sm text-foreground">
      {projects.map((p) => (
        <li key={p.name}>
          You&apos;ll join {p.name} as a {p.role === 'supervisor' ? 'Supervisor' : 'Member'}.
        </li>
      ))}
    </ul>
  );
}

function InviteHeading({ role }: { role: 'admin' | 'user' }): JSX.Element {
  return (
    <h2 className="text-base font-normal text-foreground">
      You&apos;ve been invited to Clockit as {role === 'admin' ? 'Admin' : 'User'}
    </h2>
  );
}

function AcceptForm({ token, invite, onEndState, showExistingNote = false }: AcceptFormProps): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [done, setDone] = useState<'existing' | 'login-failed' | null>(null);
  const form = useForm<AcceptValues>({ resolver: zodResolver(acceptSchema), defaultValues: { name: '', password: '', confirm: '' } });

  const accept = useMutation({
    mutationFn: (values: AcceptValues) =>
      postJson<AcceptInviteResult>('/api/invites/accept', {
        token,
        name: values.name.trim(),
        password: values.password,
        timezone: browserTimeZone(),
      }),
    meta: { silent: true },
  });

  async function onSubmit(values: AcceptValues): Promise<void> {
    setInlineError(null);
    let result: AcceptInviteResult;
    try {
      result = await accept.mutateAsync(values);
    } catch (err) {
      const endState = err instanceof ApiClientError ? ERROR_CODE_TO_STATE[err.code] : undefined;
      if (endState) {
        onEndState(endState);
        return;
      }
      setInlineError(err instanceof ApiClientError ? err.message : NETWORK_ERROR_MESSAGE);
      return;
    }
    if (result.outcome === 'existing') {
      // Never try the typed password: the existing account keeps its own password.
      setDone('existing');
      return;
    }
    try {
      await login(result.email, values.password);
      navigate('/', { replace: true });
    } catch {
      setDone('login-failed');
    }
  }

  if (done) {
    return (
      <div className="space-y-4">
        <p className="text-sm">{done === 'existing' ? EXISTING_ACCOUNT_MESSAGE : AUTO_SIGN_IN_FAILED_MESSAGE}</p>
        <Button asChild className="w-full">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </div>
    );
  }

  const errors = form.formState.errors;
  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3" noValidate>
      <InviteHeading role={invite.role} />
      <InviteProjects projects={invite.projects ?? []} />
      <div className="grid gap-1.5">
        <Label htmlFor="invite-email">Email</Label>
        <Input id="invite-email" value={invite.email} readOnly className="bg-muted" />
        {showExistingNote && <p className="text-xs text-muted-foreground">{EXISTING_ACCOUNT_NOTE}</p>}
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="invite-name">Display name</Label>
        <Input id="invite-name" autoComplete="name" {...form.register('name')} />
        {errors.name && <p className="text-xs text-danger-fg">{errors.name.message}</p>}
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="invite-password">Password</Label>
        <Input id="invite-password" type="password" autoComplete="new-password" {...form.register('password')} />
        {errors.password && <p className="text-xs text-danger-fg">{errors.password.message}</p>}
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="invite-confirm">Confirm password</Label>
        <Input id="invite-confirm" type="password" autoComplete="new-password" {...form.register('confirm')} />
        {errors.confirm && <p className="text-xs text-danger-fg">{errors.confirm.message}</p>}
      </div>
      {inlineError && (
        <p role="alert" className={ALERT_CLASSES}>
          {inlineError}
        </p>
      )}
      <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
        {form.formState.isSubmitting ? 'Creating account…' : inlineError ? 'Try again' : 'Create account'}
      </Button>
    </form>
  );
}

interface JoinCardProps {
  token: string;
  invite: { email: string; role: 'admin' | 'user'; projects?: InviteProjectLine[] };
  onEndState: (state: EndState) => void;
}

/**
 * The invited email already has a workspace account (sprint 8 §8.2): no name or password. The
 * server links the account; the person then signs in with their existing password. No password
 * is known here, so this never calls login().
 */
function JoinCard({ token, invite, onEndState }: JoinCardProps): JSX.Element {
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [accountMissing, setAccountMissing] = useState(false);
  const [joined, setJoined] = useState(false);

  const join = useMutation({
    mutationFn: () => postJson<AcceptInviteResult>('/api/invites/accept', { token, timezone: browserTimeZone() }),
    meta: { silent: true },
  });

  async function onJoin(): Promise<void> {
    setInlineError(null);
    try {
      await join.mutateAsync();
    } catch (err) {
      const endState = err instanceof ApiClientError ? ERROR_CODE_TO_STATE[err.code] : undefined;
      if (endState) {
        onEndState(endState);
        return;
      }
      if (err instanceof ApiClientError && err.code === 'ACCOUNT_NOT_FOUND') setAccountMissing(true);
      setInlineError(err instanceof ApiClientError ? err.message : NETWORK_ERROR_MESSAGE);
      return;
    }
    // Whatever the outcome, the person signs in with the password they already have.
    setJoined(true);
  }

  if (joined) {
    return (
      <div className="space-y-4" data-testid="invite-joined">
        <p className="text-sm">{JOINED_MESSAGE}</p>
        <Button asChild className="w-full">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <InviteHeading role={invite.role} />
      <InviteProjects projects={invite.projects ?? []} />
      <div className="grid gap-1.5">
        <Label htmlFor="invite-email">Email</Label>
        <Input id="invite-email" value={invite.email} readOnly className="bg-muted" />
      </div>
      <p className="text-sm">{JOIN_INTRO}</p>
      {inlineError && (
        <p role="alert" className={ALERT_CLASSES}>
          {inlineError}
        </p>
      )}
      {accountMissing ? (
        <Button type="button" className="w-full" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      ) : (
        <Button type="button" className="w-full" disabled={join.isPending} onClick={() => void onJoin()}>
          {join.isPending ? 'Joining…' : inlineError ? 'Try again' : 'Join Clockit'}
        </Button>
      )}
    </div>
  );
}

export function AcceptInvitePage(): JSX.Element {
  const { token = '' } = useParams();
  const { user, isLoading: authLoading } = useAuth();
  const [endState, setEndState] = useState<EndState | null>(null);
  const lookup = useQuery({
    queryKey: ['invite-lookup', token],
    queryFn: () => postJson<InviteLookup>('/api/invites/lookup', { token }),
    enabled: !user && !authLoading,
    retry: false,
    staleTime: Infinity,
    meta: { silent: true },
  });

  let content: JSX.Element;
  if (authLoading) content = <Skeleton className="h-40 w-full" />;
  else if (user) content = <SignedInNotice email={user.email} />;
  else if (endState) content = <EndStateScreen state={endState} />;
  else if (lookup.isLoading) content = <Skeleton className="h-40 w-full" data-testid="invite-loading" />;
  else if (lookup.isError || !lookup.data) {
    content = <p className="text-sm">Couldn&apos;t check this invite. Check your connection and reload the page.</p>;
  } else if (lookup.data.state !== 'valid') content = <EndStateScreen state={lookup.data.state} />;
  else if (lookup.data.accountExists === true) {
    content = <JoinCard token={token} invite={lookup.data.invite} onEndState={setEndState} />;
  } else {
    content = (
      <AcceptForm token={token} invite={lookup.data.invite} onEndState={setEndState} showExistingNote={lookup.data.accountExists === null} />
    );
  }

  return <PublicCardLayout>{content}</PublicCardLayout>;
}
